import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import type { ActivationCodeResponse, DayOffDto, EngineerListItem, VisitDto } from '../api/types';
import { Avatar } from '../components/Avatar';
import { ErrorStrip } from './Dashboard';
import { TaskCard } from '../components/TaskCard';
import type { ApiClient } from '../api/client';

interface Props {
  onOpenVisit(v: VisitDto): void;
}

const TG_BOT_USERNAME = '@fielddesk_evotec_bot';

export function EngineersScreen({ onOpenVisit }: Props) {
  const { api, state } = useAuth();
  const role = state.status === 'authenticated' ? state.user.role : null;
  const isAdmin = role === 'ADMIN';
  // SECURITY: коды активации выдаёт ТОЛЬКО ADMIN — раньше было ADMIN+DISPATCHER,
  // теперь повышенный приоритет (привязка Telegram → доступ к Mini App).
  const canIssueCodes = role === 'ADMIN';

  const [list, setList] = useState<EngineerListItem[]>([]);
  const [visitsByEng, setVisitsByEng] = useState<Record<number, VisitDto[]>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Состояние модалок и операций.
  const [codeModal, setCodeModal] = useState<ActivationCodeResponse | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [openMenuFor, setOpenMenuFor] = useState<number | null>(null);
  const [opErr, setOpErr] = useState<string | null>(null);
  const [purging, setPurging] = useState(false);

  const seedCount = useMemo(() => list.filter((e) => e.source === 'SEED').length, [list]);

  async function reload() {
    const today = new Date().toISOString().slice(0, 10);
    const [engs, vs] = await Promise.all([
      api.listEngineers(today),
      api.listVisits({ from: today, to: today, limit: 500 }),
    ]);
    setList(engs);
    const map: Record<number, VisitDto[]> = {};
    for (const v of vs.items) (map[v.engineer.id] ??= []).push(v);
    setVisitsByEng(map);
  }

  useEffect(() => {
    setLoading(true);
    reload()
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  // Закрытие kebab-меню по клику вне его.
  useEffect(() => {
    if (openMenuFor == null) return;
    const onDoc = () => setOpenMenuFor(null);
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [openMenuFor]);

  async function handleIssueCode(eng: EngineerListItem) {
    setBusyId(eng.id);
    setOpErr(null);
    try {
      const resp = await api.createActivationCode(eng.id);
      setCodeModal(resp);
    } catch (e) {
      setOpErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDeactivate(eng: EngineerListItem) {
    if (!window.confirm(`Точно деактивировать инженера ${eng.fullName}? Все его JWT перестанут работать.`)) return;
    setBusyId(eng.id);
    setOpErr(null);
    setOpenMenuFor(null);
    try {
      await api.deactivateUser(eng.id);
      await reload();
    } catch (e) {
      setOpErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function handleReactivate(eng: EngineerListItem) {
    setBusyId(eng.id);
    setOpErr(null);
    setOpenMenuFor(null);
    try {
      await api.reactivateUser(eng.id);
      await reload();
    } catch (e) {
      setOpErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  /** ADMIN: ACTIVE+TG → отвязать Telegram, перевести в INVITED */
  async function handleDisconnectTelegram(eng: EngineerListItem) {
    if (!window.confirm(
      `Отключить Telegram у ${eng.fullName}?\nИнженер потеряет доступ к Mini App и получит новый код активации.`,
    )) return;
    setBusyId(eng.id);
    setOpErr(null);
    setOpenMenuFor(null);
    try {
      await api.patchUserAccess(eng.id, { telegramUserId: null, activationStatus: 'INVITED' });
      await reload();
    } catch (e) {
      setOpErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  /** ADMIN: DISABLED → INVITED (без сброса Telegram, если он был) */
  async function handleActivateDisabled(eng: EngineerListItem) {
    setBusyId(eng.id);
    setOpErr(null);
    setOpenMenuFor(null);
    try {
      await api.patchUserAccess(eng.id, { activationStatus: 'INVITED' });
      await reload();
    } catch (e) {
      setOpErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  async function handlePurgeSeed() {
    if (!window.confirm(
      `Удалить всех тестовых seed-инженеров (${seedCount} шт.)? Их визиты тоже будут удалены. Действие необратимо.`,
    )) return;
    setPurging(true);
    setOpErr(null);
    try {
      const r = await api.deleteSeedEngineers();
      await reload();
      window.alert(`Удалено: ${r.deleted}`);
    } catch (e) {
      setOpErr((e as Error).message);
    } finally {
      setPurging(false);
    }
  }

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700 }}>Инженеры</h1>
        {isAdmin && seedCount > 0 && (
          <button
            onClick={handlePurgeSeed}
            disabled={purging}
            style={{
              padding: '8px 14px',
              background: purging ? '#cbd5e1' : '#dc2626',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: purging ? 'wait' : 'pointer',
            }}
          >
            {purging ? 'Удаляем…' : `🗑 Очистить тестовых (${seedCount})`}
          </button>
        )}
      </div>
      {err && <ErrorStrip message={err} />}
      {opErr && <ErrorStrip message={opErr} />}
      {loading ? (
        <div style={{ color: '#94a3b8' }}>Загрузка…</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
          {list.map((e) => {
            const visits = visitsByEng[e.id] ?? [];
            const isInvited = e.activationStatus === 'INVITED';
            const isDisabled = e.activationStatus === 'DISABLED';
            const isActive = e.activationStatus === 'ACTIVE';
            const showCodeBtn = canIssueCodes && (isInvited || (isActive && !e.hasTelegram));

            return (
              <div
                key={e.id}
                style={{
                  background: '#fff',
                  borderRadius: 12,
                  padding: 18,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                  position: 'relative',
                  opacity: isDisabled ? 0.65 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
                  <Avatar initials={e.initials} color={e.color} size={44} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.fullName}
                      </span>
                      {e.source === 'SEED' && (
                        <span
                          style={{
                            fontSize: 9,
                            background: '#fef3c7',
                            color: '#92400e',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontWeight: 700,
                          }}
                        >
                          SEED
                        </span>
                      )}
                    </div>
                    <StatusLine eng={e} />
                  </div>

                  {isAdmin && (
                    <KebabMenu
                      open={openMenuFor === e.id}
                      onToggle={(ev) => {
                        ev.stopPropagation();
                        setOpenMenuFor(openMenuFor === e.id ? null : e.id);
                      }}
                    >
                      {!isDisabled && (
                        <MenuItem onClick={() => handleDeactivate(e)} danger>
                          Деактивировать
                        </MenuItem>
                      )}
                      {isDisabled && (
                        <MenuItem onClick={() => handleReactivate(e)}>Активировать обратно</MenuItem>
                      )}
                    </KebabMenu>
                  )}
                </div>

                {/* ADMIN: ACTIVE+Telegram → отключить Telegram (красная кнопка) */}
                {isAdmin && isActive && e.hasTelegram && (
                  <button
                    onClick={() => handleDisconnectTelegram(e)}
                    disabled={busyId === e.id}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      marginBottom: 6,
                      background: busyId === e.id ? '#cbd5e1' : '#dc2626',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: busyId === e.id ? 'wait' : 'pointer',
                    }}
                  >
                    {busyId === e.id ? 'Сохраняем…' : '🔌 Отключить Telegram'}
                  </button>
                )}

                {/* ADMIN: DISABLED → Активировать (зелёная кнопка) */}
                {isAdmin && isDisabled && (
                  <button
                    onClick={() => handleActivateDisabled(e)}
                    disabled={busyId === e.id}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      marginBottom: 6,
                      background: busyId === e.id ? '#cbd5e1' : '#16a34a',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: busyId === e.id ? 'wait' : 'pointer',
                    }}
                  >
                    {busyId === e.id ? 'Активируем…' : '✅ Активировать'}
                  </button>
                )}

                {/* ADMIN: INVITED или ACTIVE без Telegram → выдать код */}
                {showCodeBtn && (
                  <button
                    onClick={() => handleIssueCode(e)}
                    disabled={busyId === e.id}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      marginBottom: 10,
                      background: busyId === e.id ? '#cbd5e1' : '#2563eb',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: busyId === e.id ? 'wait' : 'pointer',
                    }}
                  >
                    {busyId === e.id ? 'Генерируем…' : '🔑 Сгенерировать код активации'}
                  </button>
                )}

                {isAdmin && (
                  <AdminEngineerControls api={api} engineer={e} onChanged={reload} />
                )}

                {visits.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#94a3b8', padding: '10px 0' }}>Сегодня выездов нет.</div>
                ) : (
                  visits.map((v) => <TaskCard key={v.id} visit={v} onClick={onOpenVisit} compact />)
                )}
              </div>
            );
          })}
        </div>
      )}

      {codeModal && <ActivationCodeModal data={codeModal} onClose={() => setCodeModal(null)} />}
    </div>
  );
}

// --- Подпись «занят/свободен/не активирован/деактивирован» -----------------
function StatusLine({ eng }: { eng: EngineerListItem }) {
  if (eng.activationStatus === 'DISABLED') {
    return <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>● Деактивирован</div>;
  }
  if (eng.activationStatus === 'INVITED') {
    return <div style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>⚠ Не активирован (нет Telegram)</div>;
  }
  return (
    <div style={{ fontSize: 11, color: eng.status === 'busy' ? '#d97706' : '#16a34a', fontWeight: 600 }}>
      {eng.status === 'busy' ? '● Занят' : '● Свободен'} · {eng.todayVisitsCount} сегодня
    </div>
  );
}

// --- Kebab-меню (...) -------------------------------------------------------
function KebabMenu({
  open,
  onToggle,
  children,
}: {
  open: boolean;
  onToggle(ev: React.MouseEvent): void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ position: 'relative' }} onClick={(ev) => ev.stopPropagation()}>
      <button
        onClick={onToggle}
        style={{
          background: 'transparent',
          border: 'none',
          color: '#64748b',
          fontSize: 18,
          padding: '2px 8px',
          cursor: 'pointer',
          borderRadius: 6,
          lineHeight: 1,
        }}
        aria-label="Меню"
      >
        ⋮
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 26,
            right: 0,
            minWidth: 200,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function MenuItem({
  onClick,
  danger,
  children,
}: {
  onClick(): void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '10px 14px',
        background: 'transparent',
        border: 'none',
        color: danger ? '#dc2626' : '#0f172a',
        fontSize: 13,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

// --- Модалка с 6-значным кодом + копирование --------------------------------
function ActivationCodeModal({
  data,
  onClose,
}: {
  data: ActivationCodeResponse;
  onClose(): void;
}) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Авто-выделение при открытии — пользователь сразу нажимает Ctrl+C, даже
  // если кнопка «Скопировать» не отработает (sandbox iframe Bitrix).
  useEffect(() => {
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 50);
    return () => clearTimeout(t);
  }, []);

  async function copy() {
    // Двухступенчатый каскад с явным fallback'ом:
    //  1) navigator.clipboard.writeText — современный API; в Bitrix-iframe
    //     обычно блокирован (clipboard-write permission не выдан, либо
    //     insecure-context).
    //  2) document.execCommand('copy') над уже отрисованным input — иногда
    //     iframe sandbox блокирует и его (allow-same-origin без
    //     allow-clipboard-write).
    // Если оба упали — переключаемся в режим 'failed', UI остаётся
    // полнофункциональным: пользователь видит выделенный код в input
    // и инструкцию «Ctrl+C».
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(data.code);
        setCopyState('copied');
        setTimeout(() => setCopyState('idle'), 2000);
        return;
      }
    } catch {
      /* try execCommand */
    }
    try {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
        const ok = document.execCommand('copy');
        if (ok) {
          setCopyState('copied');
          setTimeout(() => setCopyState('idle'), 2000);
          return;
        }
      }
    } catch {
      /* fallthrough */
    }
    // Не вышло — выделяем поле и переключаем подпись на инструкцию.
    inputRef.current?.focus();
    inputRef.current?.select();
    setCopyState('failed');
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        onClick={(ev) => ev.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: 28,
          width: 400,
          maxWidth: '92vw',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Код активации</div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 18 }}>
          Для <b>{data.user.fullName}</b>
        </div>

        {/* readonly input с моноширинным шрифтом, выглядит как 6-цифровой
            бейдж, но это РЕАЛЬНОЕ текстовое поле — даже если clipboard API
            заблокирован, юзер выделит и скопирует Ctrl+C сам. */}
        <input
          ref={inputRef}
          readOnly
          value={data.code}
          onFocus={(ev) => ev.currentTarget.select()}
          onClick={(ev) => ev.currentTarget.select()}
          style={{
            display: 'block',
            width: '100%',
            background: '#f1f5f9',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            padding: '18px 20px',
            textAlign: 'center',
            fontSize: 36,
            fontWeight: 700,
            letterSpacing: 8,
            color: '#0f172a',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            marginBottom: 14,
            caretColor: 'transparent',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />

        <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, marginBottom: 14 }}>
          Код действителен 24 часа. Передайте инженеру для активации в Telegram-боте{' '}
          <b>{TG_BOT_USERNAME}</b>.
        </div>

        {copyState === 'failed' && (
          <div
            style={{
              background: '#fef3c7',
              border: '1px solid #fde68a',
              borderRadius: 8,
              padding: '8px 12px',
              fontSize: 12,
              color: '#92400e',
              marginBottom: 14,
              lineHeight: 1.4,
            }}
          >
            Скопируйте код вручную: код уже выделен — нажмите{' '}
            <b>Ctrl+C</b> (или <b>⌘+C</b> на Mac).
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={copy}
            style={{
              flex: 1,
              padding: '10px 14px',
              background:
                copyState === 'copied' ? '#16a34a' : copyState === 'failed' ? '#94a3b8' : '#2563eb',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {copyState === 'copied'
              ? '✓ Скопировано'
              : copyState === 'failed'
                ? 'Выделите и Ctrl+C'
                : 'Скопировать в буфер обмена'}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '10px 16px',
              background: '#fff',
              border: '1px solid #e2e8f0',
              color: '#475569',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
//  ADMIN-only block в карточке инженера: видимость + выходные дни.
// =====================================================================
function AdminEngineerControls({
  api,
  engineer,
  onChanged,
}: {
  api: ApiClient;
  engineer: EngineerListItem;
  onChanged(): Promise<void>;
}) {
  const [visible, setVisible] = useState(engineer.isVisibleInSchedule);
  const [savingVisible, setSavingVisible] = useState(false);
  const [opError, setOpError] = useState<string | null>(null);

  // При перезагрузке списка извне — sync локальный стейт.
  useEffect(() => {
    setVisible(engineer.isVisibleInSchedule);
  }, [engineer.isVisibleInSchedule]);

  async function toggleVisible() {
    const next = !visible;
    setVisible(next); // оптимистично
    setSavingVisible(true);
    setOpError(null);
    try {
      await api.updateEngineerVisibility(engineer.id, next);
      // Локальный список инженеров содержит флаг — мы обновили его в
      // памяти через setVisible. Полный reload не делаем, чтобы не дёргать
      // расписание; ОК потому что toggle никаких визитов не меняет.
    } catch (e) {
      setVisible(!next);
      setOpError((e as Error).message);
    } finally {
      setSavingVisible(false);
    }
  }

  return (
    <div
      style={{
        marginBottom: 10,
        padding: '10px 12px',
        background: '#f8fafc',
        borderRadius: 8,
        border: '1px solid #e2e8f0',
      }}
    >
      {/* --- Toggle видимости --- */}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          fontSize: 12,
          color: '#0f172a',
          cursor: savingVisible ? 'wait' : 'pointer',
          marginBottom: 8,
        }}
      >
        <span>👁 Показывать в расписании</span>
        <input
          type="checkbox"
          checked={visible}
          disabled={savingVisible}
          onChange={toggleVisible}
          style={{ width: 16, height: 16, cursor: savingVisible ? 'wait' : 'pointer' }}
        />
      </label>

      <DaysOffSection api={api} engineerId={engineer.id} onChanged={onChanged} />

      {opError && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#b91c1c' }}>{opError}</div>
      )}
    </div>
  );
}

function DaysOffSection({
  api,
  engineerId,
  onChanged: _onChanged,
}: {
  api: ApiClient;
  engineerId: number;
  onChanged(): Promise<void>;
}) {
  const [items, setItems] = useState<DayOffDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      const today = new Date();
      const from = today.toISOString().slice(0, 10);
      // Берём год вперёд — достаточно для управления отпусками/болезнями.
      const yearAhead = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())
        .toISOString()
        .slice(0, 10);
      const r = await api.listDaysOff(engineerId, { from, to: yearAhead });
      setItems(r.items);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineerId]);

  async function handleAdd() {
    if (!newDate) return;
    setBusy(true);
    setErr(null);
    try {
      await api.addDayOff(engineerId, newDate, newReason.trim() || undefined);
      setNewDate('');
      setNewReason('');
      setAdding(false);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(date: string) {
    if (!window.confirm(`Удалить выходной ${date}?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await api.removeDayOff(engineerId, date);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>
          🏖 Выходные дни {!loading && items.length > 0 ? `(${items.length})` : ''}
        </span>
        <button
          onClick={() => setAdding((v) => !v)}
          disabled={busy}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#2563eb',
            fontSize: 11,
            fontWeight: 600,
            cursor: busy ? 'wait' : 'pointer',
            padding: 0,
          }}
        >
          {adding ? '× Отмена' : '+ Добавить'}
        </button>
      </div>

      {adding && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: 8,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          <input
            type="date"
            value={newDate}
            onChange={(ev) => setNewDate(ev.target.value)}
            style={{
              padding: '5px 8px',
              border: '1px solid #e2e8f0',
              borderRadius: 4,
              fontSize: 12,
            }}
          />
          <input
            type="text"
            placeholder="Причина (опционально): Болен, Отпуск…"
            value={newReason}
            maxLength={500}
            onChange={(ev) => setNewReason(ev.target.value)}
            style={{
              padding: '5px 8px',
              border: '1px solid #e2e8f0',
              borderRadius: 4,
              fontSize: 12,
            }}
          />
          <button
            onClick={handleAdd}
            disabled={busy || !newDate}
            style={{
              padding: '5px 10px',
              background: newDate ? '#16a34a' : '#cbd5e1',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              cursor: busy || !newDate ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Сохраняем…' : 'Сохранить'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 11, color: '#94a3b8' }}>Загрузка…</div>
      ) : items.length === 0 ? (
        <div style={{ fontSize: 11, color: '#94a3b8' }}>Нет выходных дней</div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {items.map((d) => (
            <li
              key={d.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: 11,
                padding: '4px 6px',
                background: '#fff',
                borderRadius: 4,
              }}
            >
              <span style={{ color: '#0f172a' }}>
                <b>{formatDateRu(d.date)}</b>
                {d.reason ? <span style={{ color: '#64748b' }}> · {d.reason}</span> : null}
              </span>
              <button
                onClick={() => handleRemove(d.date)}
                disabled={busy}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#dc2626',
                  cursor: busy ? 'wait' : 'pointer',
                  fontSize: 14,
                  padding: 0,
                  lineHeight: 1,
                }}
                title="Удалить"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {err && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#b91c1c' }}>{err}</div>
      )}
    </div>
  );
}

function formatDateRu(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}
